import PptxGenJS from 'pptxgenjs';
import { DEFAULT_CHART_COLORS } from '../../utils';

export const FONT_SIZE_H1 = 28;
export const FONT_SIZE_H2 = 24;
export const FONT_SIZE_H3 = 20;
export const FONT_SIZE_H4 = 18;
export const FONT_SIZE_H5 = 14;
export const FONT_SIZE_H6 = 12;
export const FONT_SIZE_P = 8;
export const FONT_SIZE_DATA_LABEL = 6;
export const TEXTBOX_HEIGHT = 0.59;
export const WHITE_COLOR = 'ffffff';
export const SLIDE_FULL_WIDTH = 10;
export const FONT_FAMILY_MASTERSLIDE = 'Source Sans Pro';
export const FONT_FAMILY = 'Inter';
export const SLIDE_WIDTH = 10;
export const SLIDE_HEIGHT = 5.625;
export const HEADER_HEIGHT = 0.6;
export const FOOTER_HEIGHT = 0.4;
export const COLORS_ACCENT = '4F5868';
export const LEGEND_COLOR = '595959';
export const SUBHEADER_COLOR = 'F7F9FB';
export const COLOR_SECONDARY = 'DADFEB';

export const PAGE_START_X = 0;
export const PAGE_START_Y = 0.6;
export const SUMMARY_BAR_HEIGHT = 0.5;

export const CHART_WIDTH = 10;
export const CHART_HEIGHT = 3.2;
export const CHART_HEIGHT_NO_QUESTION = 4;
export const CHART_HEIGHT_NO_QUESTION_NO_SUMMARY_BAR = 4.4;
export const COLUMN_LABEL_Y_OFFSET = '-0.045';
export const BAR_LABEL_X_OFFSET = '0.035';
export const MIN_SEGMENT_SIZE = 0.15;
export const CHAR_WIDTH_6PT = 0.045;
export const QUESTION_SUBHEADER_WIDTH = '100%';
export const QUESTION_SUBHEADER_HEIGHT = 0.53;
export const QUESTION_PARENT_WIDTH = 2.4;
export const QUESTION_WIDTH = 7.5;

export const AGGREGATE_STAT_OPTIONS = {
  fontSize: FONT_SIZE_H5,
  bold: true,
  color: COLORS_ACCENT,
  fontFace: FONT_FAMILY,
};

export const AGGREGATE_CAPTION_OPTIONS = {
  fontSize: FONT_SIZE_P,
  color: COLORS_ACCENT,
  fontFace: FONT_FAMILY,
};

export const SUMMARY_BAR_START_X = PAGE_START_X + 0.17;
export const AGGREGATE_START_X = SUMMARY_BAR_START_X + 0.2;
export const SUMMARY_BAR_START_Y = PAGE_START_Y + QUESTION_SUBHEADER_HEIGHT + 0.15;
export const SUMMARY_BAR_RADIUS = 0.1;
export const CHART_START_Y = PAGE_START_Y + SUMMARY_BAR_START_Y + 0.1;

export const centerX = (SLIDE_WIDTH - CHART_WIDTH) / 2;
export const centerY =
  HEADER_HEIGHT + (SLIDE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT - CHART_HEIGHT) / 2;

export const HEADER_TEXT_STYLE = {
  x: 0,
  y: 0,
  w: SLIDE_FULL_WIDTH,
  fontSize: FONT_SIZE_H5,
  bold: false,
  h: TEXTBOX_HEIGHT,
  color: WHITE_COLOR,
  fontFace: FONT_FAMILY,
};
export const HEADER_TEXT_TITLE_STYLE = {
  x: 1.7,
  y: 0,
  w: 7,
  h: 0.59,
  fontSize: FONT_SIZE_H4,
  bold: false,
  color: WHITE_COLOR,
  fontFace: FONT_FAMILY,
};
export const CHART_DEFAULT_STYLE = (
  colorPalette?: string[],
  dataLength?: number
): PptxGenJS.IChartOpts & PptxGenJS.IChartPropsLegend => ({
  dataLabelFontFace: FONT_FAMILY,
  dataLabelPosition: 't' as const,
  dataLabelFontSize: 10,
  dataLabelColor: WHITE_COLOR,
  dataLabelFontBold: true,
  x: centerX,
  y: centerY,
  w: CHART_WIDTH,
  h: CHART_HEIGHT,
  showLegend: true,
  legendPos: 'b' as const,
  legendFontSize: 12,
  legendColor: LEGEND_COLOR,
  chartColors:
    colorPalette && colorPalette.length > 0
      ? colorPalette.map((c): string => c.replace('#', '')).slice(0, dataLength)
      : DEFAULT_CHART_COLORS.map((c): string => c.replace('#', '')).slice(0, dataLength),
  barDir: 'col', //col
  barGapWidthPct: 100,
  showValue: true,
  showCatAxisTitle: false,
  chartArea: {
    fill: { color: WHITE_COLOR },
  },
  catAxisLabelColor: COLORS_ACCENT,
  catAxisLabelFontSize: 9,
  catAxisLineColor: 'd0d4d7',
  catAxisLineSize: 0.5,

  showValAxisTitle: false,
  valAxisLabelFontSize: 9,
  valAxisLabelColor: COLORS_ACCENT,
  valAxisLineColor: WHITE_COLOR,
  valAxisLineSize: 0.5,

  valGridLine: {
    color: 'd0d4d7',
    size: 0.5,
    style: 'solid',
  },

  // If you also want to style vertical gridlines (category axis)
  // catGridLine: {
  //   color: 'd0d4d7',
  //   size: 0.3,
  //   style: 'solid', // or 'dash', 'dot', etc.
  // },
});

export const CHART_PIE_DEFAULT_STYLE = (colorPalette?: string[]) => ({
  dataLabelFontFace: FONT_FAMILY,
  dataLabelPosition: 't' as const,
  x: centerX,
  y: centerY,
  w: CHART_WIDTH,
  h: CHART_HEIGHT,
  showLegend: true,
  legendPos: 'b' as const,
  legendLayout: 'horizontal',
  legendFontSize: 12,
  chartArea: { fill: { color: WHITE_COLOR } },
  legendColor: LEGEND_COLOR,
  dataLabelFontSize: 8,
  dataLabelColor: 'F9FAFC',
  chartColors: colorPalette
    ? colorPalette.map((c): string => c.replace('#', ''))
    : DEFAULT_CHART_COLORS.map((c): string => c.replace('#', '')),
  // barDir: 'col',
  showValue: true,
});

export enum MarkdownNodeType {
  Heading = 'heading',
  Paragraph = 'paragraph',
  List = 'list',
  Text = 'text',
}

export const MAP_GRAPHIC_XML = `<mc:AlternateContent>
    <mc:Choice Requires="cx1">
        <p:graphicFrame>
            <p:nvGraphicFramePr>
                <p:cNvPr id="144" name="ChartObject" />
                <p:cNvGraphicFramePr />
                <p:nvPr />
            </p:nvGraphicFramePr>
            <p:xfrm>
                <a:off x="219456" y="1864422" />
                <a:ext cx="8229600" cy="2981898" />
            </p:xfrm>
            <a:graphic>
                <a:graphicData uri="http://schemas.microsoft.com/office/drawing/2014/chartex">
                        <cx:chart xmlns:cx="http://schemas.microsoft.com/office/drawing/2014/chartex" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId5" />
                </a:graphicData>
            </a:graphic>
        </p:graphicFrame>
    </mc:Choice>
    <mc:Fallback>
       <p:sp>
        <p:nvSpPr>
            <p:cNvPr id="45" name="Placeholder" />
            <p:cNvSpPr />
            <p:nvPr />
        </p:nvSpPr>
        <p:spPr />
        <p:txBody>
            <a:bodyPr />
            <a:lstStyle />
            <a:p>
                <a:r>
                    <a:t>Unsupported Chart</a:t>
                </a:r>
            </a:p>
        </p:txBody>
    </p:sp>
    </mc:Fallback>
</mc:AlternateContent>`;

export const MAP_RELS = `<?xml version="1.0" encoding="utf-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
    <Relationship Id="rId1"
        Type="http://schemas.microsoft.com/office/2011/relationships/chartColorStyle"
        Target="colors1.xml" />
    <Relationship Id="rId2" Type="http://schemas.microsoft.com/office/2011/relationships/chartStyle"
        Target="style1.xml" />
</Relationships>`;

export const MAP_COLORS = `<cs:colorStyle xmlns:cs="http://schemas.microsoft.com/office/drawing/2012/chartStyle"
    xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" meth="cycle" id="10">
    <a:schemeClr val="accent1" />
    <a:schemeClr val="accent2" />
    <a:schemeClr val="accent3" />
    <a:schemeClr val="accent4" />
    <a:schemeClr val="accent5" />
    <a:schemeClr val="accent6" />
    <cs:variation />
    <cs:variation>
        <a:lumMod val="60000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="80000" />
        <a:lumOff val="20000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="80000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="60000" />
        <a:lumOff val="40000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="50000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="70000" />
        <a:lumOff val="30000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="70000" />
    </cs:variation>
    <cs:variation>
        <a:lumMod val="50000" />
        <a:lumOff val="50000" />
    </cs:variation>
</cs:colorStyle>`;

export const MAP_STYLE = `<cs:chartStyle xmlns:cs="http://schemas.microsoft.com/office/drawing/2012/chartStyle"
    xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" id="366">
    <cs:axisTitle>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1197" />
    </cs:axisTitle>
    <cs:categoryAxis>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
        <cs:defRPr sz="1197" />
    </cs:categoryAxis>
    <cs:chartArea mods="allowNoFillOverride allowNoLineOverride">
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="bg1" />
            </a:solidFill>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
        <cs:defRPr sz="1330" />
    </cs:chartArea>
    <cs:dataLabel>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1197" />
    </cs:dataLabel>
    <cs:dataLabelCallout>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="dk1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="lt1" />
            </a:solidFill>
            <a:ln>
                <a:solidFill>
                    <a:schemeClr val="dk1">
                        <a:lumMod val="25000" />
                        <a:lumOff val="75000" />
                    </a:schemeClr>
                </a:solidFill>
            </a:ln>
        </cs:spPr>
        <cs:defRPr sz="1197" />
        <cs:bodyPr rot="0" spcFirstLastPara="1" vertOverflow="clip" horzOverflow="clip" vert="horz"
            wrap="square" lIns="36576" tIns="18288" rIns="36576" bIns="18288" anchor="ctr"
            anchorCtr="1">
            <a:spAutoFit />
        </cs:bodyPr>
    </cs:dataLabelCallout>
    <cs:dataPoint>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0">
            <cs:styleClr val="auto" />
        </cs:fillRef>
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="phClr" />
            </a:solidFill>
        </cs:spPr>
    </cs:dataPoint>
    <cs:dataPoint3D>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0">
            <cs:styleClr val="auto" />
        </cs:fillRef>
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="phClr" />
            </a:solidFill>
        </cs:spPr>
    </cs:dataPoint3D>
    <cs:dataPointLine>
        <cs:lnRef idx="0">
            <cs:styleClr val="auto" />
        </cs:lnRef>
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="28575" cap="rnd">
                <a:solidFill>
                    <a:schemeClr val="phClr" />
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:dataPointLine>
    <cs:dataPointMarker>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0">
            <cs:styleClr val="auto" />
        </cs:fillRef>
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="phClr" />
            </a:solidFill>
            <a:ln w="9525">
                <a:solidFill>
                    <a:schemeClr val="lt1" />
                </a:solidFill>
            </a:ln>
        </cs:spPr>
    </cs:dataPointMarker>
    <cs:dataPointMarkerLayout symbol="circle" size="5" />
    <cs:dataPointWireframe>
        <cs:lnRef idx="0">
            <cs:styleClr val="auto" />
        </cs:lnRef>
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="28575" cap="rnd">
                <a:solidFill>
                    <a:schemeClr val="phClr" />
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:dataPointWireframe>
    <cs:dataTable>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
            </a:ln>
        </cs:spPr>
        <cs:defRPr sz="1197" />
    </cs:dataTable>
    <cs:downBar>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="dk1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="dk1">
                    <a:lumMod val="65000" />
                    <a:lumOff val="35000" />
                </a:schemeClr>
            </a:solidFill>
            <a:ln w="9525">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="65000" />
                        <a:lumOff val="35000" />
                    </a:schemeClr>
                </a:solidFill>
            </a:ln>
        </cs:spPr>
    </cs:downBar>
    <cs:dropLine>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="35000" />
                        <a:lumOff val="65000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:dropLine>
    <cs:errorBar>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="65000" />
                        <a:lumOff val="35000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:errorBar>
    <cs:floor>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
    </cs:floor>
    <cs:gridlineMajor>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:gridlineMajor>
    <cs:gridlineMinor>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:gridlineMinor>
    <cs:hiLoLine>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="75000" />
                        <a:lumOff val="25000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:hiLoLine>
    <cs:leaderLine>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="35000" />
                        <a:lumOff val="65000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:leaderLine>
    <cs:legend>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1197" />
    </cs:legend>
    <cs:plotArea mods="allowNoFillOverride allowNoLineOverride">
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
    </cs:plotArea>
    <cs:plotArea3D mods="allowNoFillOverride allowNoLineOverride">
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
    </cs:plotArea3D>
    <cs:seriesAxis>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat" cmpd="sng" algn="ctr">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
        <cs:defRPr sz="1197" />
    </cs:seriesAxis>
    <cs:seriesLine>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="9525" cap="flat">
                <a:solidFill>
                    <a:srgbClr val="D9D9D9" />
                </a:solidFill>
                <a:round />
            </a:ln>
        </cs:spPr>
    </cs:seriesLine>
    <cs:title>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1862" />
    </cs:title>
    <cs:trendline>
        <cs:lnRef idx="0">
            <cs:styleClr val="auto" />
        </cs:lnRef>
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
        <cs:spPr>
            <a:ln w="19050" cap="rnd">
                <a:solidFill>
                    <a:schemeClr val="phClr" />
                </a:solidFill>
                <a:prstDash val="sysDash" />
            </a:ln>
        </cs:spPr>
    </cs:trendline>
    <cs:trendlineLabel>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1197" />
    </cs:trendlineLabel>
    <cs:upBar>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="dk1" />
        </cs:fontRef>
        <cs:spPr>
            <a:solidFill>
                <a:schemeClr val="lt1" />
            </a:solidFill>
            <a:ln w="9525">
                <a:solidFill>
                    <a:schemeClr val="tx1">
                        <a:lumMod val="15000" />
                        <a:lumOff val="85000" />
                    </a:schemeClr>
                </a:solidFill>
            </a:ln>
        </cs:spPr>
    </cs:upBar>
    <cs:valueAxis>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1">
                <a:lumMod val="65000" />
                <a:lumOff val="35000" />
            </a:schemeClr>
        </cs:fontRef>
        <cs:defRPr sz="1197" />
    </cs:valueAxis>
    <cs:wall>
        <cs:lnRef idx="0" />
        <cs:fillRef idx="0" />
        <cs:effectRef idx="0" />
        <cs:fontRef idx="minor">
            <a:schemeClr val="tx1" />
        </cs:fontRef>
    </cs:wall>
</cs:chartStyle>`;
